import type { CityConfig } from "../city-template/types";

export const arlingtonVaConfig: CityConfig = {
  city: "Arlington",
  state: "Virginia",
  stateAbbr: "VA",
  slug: "arlington-va",
  route: "/cities/arlington-va",
  title: "Emergency Passport Arlington, VA | Expedited Passport Services | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Arlington, VA — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Rosslyn, Ballston, Clarendon, Crystal City, Pentagon City, and all of Arlington County. Get your passport fast!",
  h1: "Emergency & Expedited Passport Services in Arlington, VA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Arlington and Northern Virginia travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Stunning%20panoramic%20photograph%20of%20Arlington%20Virginia%20skyline%20at%20dusk%20with%20Rosslyn%20high-rise%20buildings%20and%20the%20Potomac%20River%2C%20modern%20glass%20towers%20reflecting%20warm%20sunset%20light%2C%20Iwo%20Jima%20Memorial%20in%20the%20foreground%2C%20lush%20green%20parks%20along%20the%20riverbank%2C%20clean%20twilight%20sky%20with%20deep%20blue%20and%20amber%20tones%2C%20professional%20cityscape%20photography%20with%20sharp%20architectural%20details%2C%20no%20text%20or%20signs%20on%20buildings&width=1800&height=700&seq=arlington-va-hero-001&orientation=landscape",
  heroImageAlt: "Arlington Virginia skyline at dusk with Rosslyn towers and the Potomac River",
  geoPosition: "38.8816;-77.0910",
  geoPlacename: "Arlington, Virginia",
  regionalAgency: {
    name: "Washington Passport Agency",
    address: "600 19th Street NW, Washington, DC 20006",
    distance: "Located in downtown Washington, DC — approximately 3 miles from Arlington via I-66",
  },
  nearbyCitiesZips: [
    { name: "Arlington", zips: "22201–22217" },
    { name: "Rosslyn", zips: "22209" },
    { name: "Ballston", zips: "22203" },
    { name: "Clarendon", zips: "22201" },
    { name: "Courthouse", zips: "22201" },
    { name: "Crystal City", zips: "22202" },
    { name: "Pentagon City", zips: "22202" },
    { name: "Shirlington", zips: "22206" },
    { name: "Columbia Pike", zips: "22204" },
    { name: "Lyon Park", zips: "22201" },
    { name: "Cherrydale", zips: "22207" },
    { name: "Virginia Square", zips: "22201" },
    { name: "Westover", zips: "22205" },
    { name: "Glebe", zips: "22207" },
    { name: "Yorktown", zips: "22207" },
    { name: "Falls Church", zips: "22040–22046" },
    { name: "McLean", zips: "22101, 22102" },
    { name: "Alexandria", zips: "22301–22315" },
    { name: "Tysons", zips: "22102" },
    { name: "Vienna", zips: "22180–22182" },
    { name: "Fairfax", zips: "22030–22039" },
    { name: "Great Falls", zips: "22066" },
    { name: "Reston", zips: "20190–20194" },
    { name: "Herndon", zips: "20170–20172" },
    { name: "Annandale", zips: "22003" },
    { name: "Springfield", zips: "22150–22153" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Arlington departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-map-pin-2-line", title: "NoVA Hub", desc: "Serving Arlington, Rosslyn, Ballston, Crystal City, and all of NoVA" },
  ],
  internalLinks: [
    { name: "Virginia state passport services", route: "/states/virginia" },
    { name: "McLean VA passport services", route: "/cities/mclean-va" },
    { name: "Alexandria VA passport services", route: "/cities/alexandria-va" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Arlington remotely via secure courier. We do not have a walk-in office in Arlington — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Arlington County travelers.",
  fpcOfficeDistance: "Serving all of Arlington County including Rosslyn, Ballston, Clarendon, Courthouse, Crystal City, Pentagon City, Shirlington, Columbia Pike, and surrounding NoVA communities",
  serviceAreaIntro: "From the high-rise corridors of Rosslyn and Ballston to the historic neighborhoods of Clarendon and Courthouse, from Crystal City to Pentagon City, from Columbia Pike to Cherrydale — Fast Passport Center serves every corner of Arlington County. We regularly help travelers from Falls Church, McLean, Tysons, Vienna, and Alexandria get their passports fast.",
  nearbyCitiesIntro: "We serve all Arlington neighborhoods and the surrounding Northern Virginia metro area including Falls Church, McLean, Tysons, Vienna, Fairfax, Great Falls, Reston, Herndon, Annandale, Springfield, and Alexandria. If you are anywhere in the Arlington County or NoVA region, we have you covered.",
  howItWorksIntro: "Most Arlington travelers complete everything online and ship documents overnight. No need to drive into Washington, DC or wait in line at the regional agency.",
  mistakesIntro: "Arlington travelers often face the temptation to drive to the Washington regional passport agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Arlington and Northern Virginia travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Arlington? Here are the most common ones we hear from Arlington County travelers.",
  statePageRoute: "/states/virginia",
  statePageName: "Virginia",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Arlington international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Jennifer K.",
      location: "Ballston, VA",
      rating: 5,
      text: "My passport was expired and I had a business trip to London in five days. Fast Passport Center had my renewal processed in 3 days. I shipped from the Ballston UPS and never had to go into DC. Absolutely worth every penny.",
      service: "Passport Renewal",
    },
    {
      name: "David M.",
      location: "Crystal City, VA",
      rating: 5,
      text: "Emergency passport for a family medical emergency overseas. I was terrified I'd have to drive to the DC agency and wait all day. Fast Passport Center got it done in 36 hours. The team kept me updated every step.",
      service: "Emergency Passport",
    },
    {
      name: "Sarah L.",
      location: "Clarendon, VA",
      rating: 5,
      text: "First passport for my twins before our spring break trip to Italy. Fast Passport Center walked us through the parental consent requirements and had both passports in 5 days. Zero stress.",
      service: "Child Passport",
    },
    {
      name: "Michael R.",
      location: "Rosslyn, VA",
      rating: 5,
      text: "Lost my passport two days before a conference in Tokyo. Fast Passport Center had a replacement in my hands in 28 hours. I still don't understand how they did it, but I'm not questioning it.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "arlington-va-1",
      name: "Arlington Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "3118 Washington Blvd",
      city: "Arlington",
      state: "VA",
      zip: "22201",
      phone: "(703) 527-1204",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Central Arlington, near Clarendon Metro",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Arlington facility. High volume — appointments strongly recommended. Call ahead to confirm passport acceptance.",
      icon: "ri-mail-line",
    },
    {
      id: "arlington-va-2",
      name: "Crystal City Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "535 15th St S",
      city: "Arlington",
      state: "VA",
      zip: "22202",
      phone: "(703) 415-0932",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Crystal City, near Pentagon City",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Crystal City and Pentagon City area. Full passport services with appointment.",
      icon: "ri-mail-line",
    },
    {
      id: "arlington-va-3",
      name: "Falls Church Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "7777 Maple Pl",
      city: "Falls Church",
      state: "VA",
      zip: "22043",
      phone: "(703) 534-4621",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Falls Church, west of Arlington",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Falls Church and west Arlington. Full passport services with appointment.",
      icon: "ri-mail-line",
    },
    {
      id: "arlington-va-4",
      name: "McLean Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1361 Chain Bridge Rd",
      city: "McLean",
      state: "VA",
      zip: "22101",
      phone: "(703) 356-4216",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "McLean, north of Arlington",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves McLean and Tysons area. Full passport services with appointment.",
      icon: "ri-mail-line",
    },
    {
      id: "arlington-va-5",
      name: "Alexandria Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1100 First St",
      city: "Alexandria",
      state: "VA",
      zip: "22314",
      phone: "(703) 549-5938",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Old Town Alexandria, south of Arlington",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Alexandria and south Arlington. Full passport services with appointment.",
      icon: "ri-mail-line",
    },
    {
      id: "arlington-va-6",
      name: "Fairfax County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "4110 Chain Bridge Rd",
      city: "Fairfax",
      state: "VA",
      zip: "22030",
      phone: "(703) 691-7320",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "Fairfax County Government Center",
      website: "https://www.fairfaxcounty.gov/",
      websiteLabel: "Fairfax County Clerk",
      notes: "Major county facility for Fairfax County. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — drive into downtown DC", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Arlington photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Arlington County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Arlington applicants show up with incorrect payment and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Washington DC regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Arlington families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Arlington travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Arlington." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Arlington address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Arlington before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Arlington for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the drive into downtown Washington, DC." },
    { q: "How fast can I renew my passport in Arlington?", a: "Fast Passport Center offers fast passport renewal in Arlington in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What is the closest passport agency to Arlington?", a: "The nearest regional passport agency to Arlington is the Washington Passport Agency, located approximately 3 miles away in downtown Washington, DC. For urgent processing without navigating DC traffic and parking, Fast Passport Center's registered courier service achieves 24–48 hour turnaround from Arlington without any in-person agency visit required." },
    { q: "Can I get a same-day passport appointment near Arlington?", a: "Same-day passport appointments are only available at the Washington Regional Passport Agency — just minutes across the river — for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Arlington residents." },
    { q: "Can I expedite a child passport in Arlington?", a: "Yes. Fast Passport Center expedites child passports in Arlington for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Arlington families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Arlington with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "How fast can I get a passport in Arlington?", a: "With Fast Passport Center's emergency courier service, Arlington travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to drive to the Washington Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities in Arlington?", a: "Popular options include the Arlington Main Post Office (22201), Crystal City Post Office (22202), Falls Church Post Office (22043), McLean Post Office (22101), Alexandria Main Post Office (22314), and Fairfax County Clerk (22030). All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "Can I get a same-day passport in Arlington?", a: "Same-day passports are only available at the Washington Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option — without the DC traffic and parking hassle." },
    { q: "What if I live in a smaller NoVA town?", a: "We serve the entire Northern Virginia region including Falls Church, McLean, Tysons, Vienna, Fairfax, Great Falls, Reston, Herndon, Annandale, Springfield, and Alexandria. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed." },
    { q: "How much does expedited passport service cost in Arlington?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline." },
    { q: "Do you help with passport photos in Arlington?", a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx Office locations in Arlington, Ballston, and Crystal City offer passport photo services. We review your photos before submission to ensure they meet State Department standards." },
    { q: "What documents do I need for a first-time passport in Arlington?", a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk." },
    { q: "Can you help with a child's passport in Arlington?", a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Arlington families through the entire process and help schedule acceptance clerk appointments." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Ballston", address: "900 N Taylor St, Arlington, VA 22203", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Clarendon", address: "3033 Wilson Blvd, Arlington, VA 22201", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Rosslyn", address: "1700 N Moore St, Arlington, VA 22209", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "CVS Pharmacy — Crystal City", address: "529 23rd St S, Arlington, VA 22202", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Falls Church", address: "311 E Broad St, Falls Church, VA 22046", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Tysons", address: "7930 Tysons Corner Center, McLean, VA 22102", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "CVS Pharmacy — Alexandria", address: "400 S Washington St, Alexandria, VA 22314", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Fairfax", address: "10430 Main St, Fairfax, VA 22030", hours: "Mon–Sun: 7AM–11PM" },
  ],
  dmvLocations: [
    {
      name: "Virginia DMV — Arlington",
      address: "4150 S Four Mile Run Dr, Arlington, VA 22204",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Driver's license and REAL ID services. Appointment recommended.",
      featured: true,
    },
    {
      name: "Virginia DMV — Falls Church",
      address: "104 W Broad St, Falls Church, VA 22046",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and passport-related ID services. Appointment strongly recommended.",
      featured: false,
    },
    {
      name: "Virginia DMV — Fairfax",
      address: "4110 Chain Bridge Rd, Fairfax, VA 22030",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Serves Fairfax County and west Arlington residents. REAL ID available.",
      featured: false,
    },
    {
      name: "Virginia DMV — Alexandria",
      address: "2681 Mill Rd, Alexandria, VA 22314",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Serves Alexandria and south Arlington. REAL ID available.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "DCA",
      name: "Ronald Reagan Washington National Airport",
      distance: "Located in Arlington, VA — minutes from downtown",
      address: "2401 Smith Ave, Arlington, VA 22202",
      note: "The primary airport for Arlington residents. Direct flights to major domestic destinations and international connections.",
      flightUrl: "https://www.flyreagan.com/",
      flightLabel: "DCA Airport",
      featured: true,
    },
    {
      code: "IAD",
      name: "Dulles International Airport",
      distance: "25 miles west of Arlington",
      address: "1 Saarinen Cir, Dulles, VA 20166",
      note: "Major international hub for long-haul flights to Europe, Asia, and the Caribbean. Accessible via I-66 and Dulles Toll Road.",
      flightUrl: "https://www.flydulles.com/",
      flightLabel: "IAD Airport",
      featured: false,
    },
    {
      code: "BWI",
      name: "Baltimore/Washington International Airport",
      distance: "45 miles northeast of Arlington",
      address: "7050 Friendship Rd, Baltimore, MD 21240",
      note: "Alternative international hub for international flights. Accessible via I-95.",
      flightUrl: "https://www.bwiairport.com/",
      flightLabel: "BWI Airport",
      featured: false,
    },
  ],
};